import { format } from "date-fns";
import { useEffect, useMemo, useReducer } from "react";
import { MeterPointConfig } from "../../../api/meters";
import { getMeterPointConfig } from "../../../api/services/meterpoints/config";
import { useQueryCache } from "react-query";
import { useProperty } from "./useProperty";

type Action =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "error"; error: Error }
  | { type: "success"; data: any };
type State = {
  status: "idle" | "loading" | "error" | "success";
  data: MeterPointConfig[] | null;
  error: Error | null;
};
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "idle":
      return {
        status: "idle",
        data: null,
        error: null,
      };
    case "loading": {
      return {
        ...state,
        status: "loading",
      };
    }
    case "error":
      return {
        ...state,
        status: "error",
        error: action.error,
      };
    case "success":
      return {
        status: "success",
        data: action.data,
        error: null,
      };
  }
};

export const useMeterPointsForProperty = (
  propertyId: string,
  accountNumber: string,
  date?: Date,
) => {
  const queryCache = useQueryCache();
  const { data: property } = useProperty({ accountNumber, propertyId });

  const [{ data, error, status }, dispatch] = useReducer(reducer, {
    status: "idle",
    error: null,
    data: null,
  });

  const meterPointIds: string[] = useMemo(
    () => property?.meterPoints?.map((mp) => mp.id) ?? [],
    [property],
  );

  useEffect(() => {
    dispatch({ type: "loading" });

    (async () => {
      if (meterPointIds.length) {
        try {
          const configs = await Promise.all(
            meterPointIds.map(async (id) => {
              const formattedDate = date
                ? format(date, "yyyy-MM-dd")
                : undefined;

              let keyParams: { meterPointId: string; date?: string } = {
                meterPointId: id,
              };
              if (formattedDate) keyParams.date = formattedDate;

              const existingCacheData = queryCache.getQueryData([
                "meterpoints/config",
                keyParams,
              ]);

              if (existingCacheData) {
                return existingCacheData;
              } else {
                return await queryCache.fetchQuery(
                  ["meterpoints/config", keyParams],
                  getMeterPointConfig,
                );
              }
            }),
          );
          dispatch({ type: "success", data: configs });
        } catch (error) {
          dispatch({ type: "error", error: error });
        }
      }
    })();
  }, [meterPointIds, date, dispatch, queryCache]);

  return useMemo(() => ({ data, status, error }), [data, status, error]);
};
